import { shouldUseLocalizedShortcuts, type Hotkeys } from "owa-hotkeys";
import { getCurrentCulture } from "owa-localize";
import type { LocalizedComposeCommand } from "./composeHotkeyDefinitions";

type ComposeShortcutLocale = "da" | "de" | "es" | "fr" | "it" | "nb" | "nn" | "pt" | "pt-pt" | "sv";

const localizedComposeHotkeys: Record<ComposeShortcutLocale, Partial<Record<LocalizedComposeCommand, Hotkeys>>> = {
    da: {
        bold: "ctrl+f",
        italic: "ctrl+k",
        insertHyperlink: "ctrl+i",
    },
    de: {
        bold: "ctrl+shift+f",
        italic: "ctrl+shift+k",
        underline: "ctrl+u",
    },
    es: {
        bold: "ctrl+n",
        italic: "ctrl+k",
        underline: "ctrl+s",
        saveDraft: "ctrl+g",
        insertHyperlink: "ctrl+alt+k",
    },
    fr: {
        bold: "ctrl+g",
    },
    it: {
        bold: "ctrl+g",
        italic: "ctrl+i",
        underline: "ctrl+s",
        saveDraft: "ctrl+b",
    },
    nb: {
        bold: "ctrl+f",
        italic: "ctrl+k",
        insertHyperlink: "ctrl+i",
    },
    nn: {
        bold: "ctrl+f",
        italic: "ctrl+k",
        insertHyperlink: "ctrl+i",
    },
    pt: {
        bold: "ctrl+n",
        underline: "ctrl+s",
        saveDraft: "ctrl+b",
    },
    "pt-pt": {
        bold: "ctrl+n",
        underline: "ctrl+s",
        saveDraft: "ctrl+g",
    },
    sv: {
        bold: "ctrl+f",
        italic: "ctrl+k",
        insertHyperlink: "ctrl+i",
    },
};

/** Resolves the localized compose hotkey, falling back to the supplied default. */
export function resolveComposeHotkey(command: LocalizedComposeCommand, defaultHotkey: Hotkeys, currentCulture?: string): Hotkeys {
    if (!shouldUseLocalizedShortcuts()) {
        return defaultHotkey;
    }
    
    const culture = (currentCulture ?? getCurrentCulture()).toLowerCase();
    const language = culture.split("-")[0];
    const shortcutLocale = (localizedComposeHotkeys[culture as ComposeShortcutLocale] ? culture : language) as ComposeShortcutLocale;
    
    // German Monarch also supports Ctrl+Shift+U for Underline.
    if (shortcutLocale == "de" && command == "underline") {
        return ["ctrl+u", "ctrl+shift+u"];
    }
    
    return localizedComposeHotkeys[shortcutLocale]?.[command] ?? defaultHotkey;
}
